import React from 'react';

import ReactSelect, { components, MenuPosition, createFilter } from 'react-select';
import { Form, Image } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { isArray } from 'lodash';

import { Colors } from 'constants/colors';
import { hexToRgb, LightenDarkenColor } from 'utils/Colors';
import { ClosableBadge } from 'components/UI';
import AvatarNameColor from 'components/UI/AvatarNameColor';
import { ExitPopupLightIcon } from 'assets/icons';

type IDropdownVariant = 'primary' | 'transparent' | 'form';

export interface IGroupedSelectOptions {
  label: string;
  options: ISelectOption[];
}

export interface DropdownProps {
  options: ISelectOption[] | ISelectGroupedOptions[];
  defaultValue?: ISelectOption;
  onChange?: any;
  className?: string;
  placeholder?: string;
  variant?: IDropdownVariant;
  value?: ISelectOption | ISelectOption[] | null; // pass an array if using isMulti
  name?: string;
  onBlur?: any;
  isDisabled?: boolean;
  isMulti?: boolean;
  icon?: any;
  isSearchable?: boolean;
  isClearable?: boolean;
  formatOptionLabel?: any;
  useImage?: boolean;
  useIcon?: boolean;
  useAvatarNameColor?: boolean;
  iconRoundedCircle?: boolean;
  maxWidth?: number;
  minWidth?: number;
  isPrependInput?: boolean;
  isInvalid?: boolean;
  label?: string;
  useBadge?: boolean;
  isLoading?: boolean;
  fitContent?: boolean;
  limitHeight?: boolean;
  menuPosition?: MenuPosition;
  menuPortalTarget?: HTMLElement;
  containerNoWrap?: boolean;
  dataTestId?: string;
  leftAlign?: boolean;
  wrapLines?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  groupedOptions?: boolean;
  [key: string]: any;
}

const Dropdown = ({
  variant = 'primary',
  useImage,
  useIcon,
  useAvatarNameColor,
  iconRoundedCircle,
  icon,
  isInvalid,
  useBadge,
  fitContent,
  limitHeight,
  menuPosition = 'absolute',
  menuPortalTarget,
  containerNoWrap,
  dataTestId,
  leftAlign,
  groupedOptions,
  ...props
}: DropdownProps) => {
  const intl = useIntl();
  const getColors = (variant: IDropdownVariant) => {
    switch (variant) {
      case 'primary':
        return {
          text: Colors.WHITE,
          background: Colors.BLUE
        };
      default:
        return {
          text: Colors.GRAY,
          background: 'transparent'
        };
    }
  };
  const colors = getColors(variant);
  const theme = (theme: any) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary: Colors.BLUE,
      primary75: hexToRgb(Colors.BLUE, 0.75),
      primary50: hexToRgb(Colors.BLUE, 0.5),
      primary25: hexToRgb(Colors.BLUE, 0.25),
      neutral0: 'white',
      neutral5: hexToRgb(Colors.GRAY, 0.1),
      neutral10: hexToRgb(Colors.GRAY, 0.2),
      neutral20: hexToRgb(Colors.GRAY, 0.3),
      neutral30: hexToRgb(Colors.GRAY, 0.4),
      neutral40: hexToRgb(Colors.GRAY, 0.5),
      neutral50: hexToRgb(Colors.GRAY, 0.6),
      neutral60: hexToRgb(Colors.GRAY, 0.7),
      neutral70: hexToRgb(Colors.GRAY, 0.8),
      neutral80: hexToRgb(Colors.GRAY, 0.9),
      neutral90: hexToRgb(Colors.GRAY, 1)
    }
  });
  const styles = {
    container: (provided: any, state: any) => ({
      ...provided,
      pointerEvents: 'auto',
      opacity: state.isDisabled ? 0.6 : 1,
      textAlign: 'left',
      maxWidth: props.maxWidth || 'auto',
      minWidth: props.minWidth || 'auto',
      maxHeight: props.isPrependInput ? '2.25rem' : 'auto',
      width: fitContent ? 'fit-content' : provided.width
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      borderColor: isInvalid ? Colors.RED : provided.borderColor,
      borderWidth: variant === 'form' ? 1 : 0,
      boxShadow: '0!important',
      backgroundColor: colors.background,
      borderRadius: state.menuIsOpen ? '4px 4px 0 0' : 4,
      width: '100%',
      minHeight: '2rem'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      maxHeight: props.isPrependInput ? '2.25rem' : 'auto'
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: colors.text,
      position: containerNoWrap ? 'static' : provided.position,
      transform: containerNoWrap ? 'none' : provided.transform,
      maxWidth: containerNoWrap ? 'none' : provided.maxWidth
    }),
    multiValue: (provided: any, { data }: any) => ({
      ...provided,
      backgroundColor: data.color,
      fontWeight: '400',
      fontSize: '1rem'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: colors.text,
      fontWeight: '400',
      fontSize: '1rem'
    }),
    menu: (provided: any) => ({
      ...provided,
      top: 'unset',
      marginTop: 0,
      borderRadius: '0 0 4px 4px',
      zIndex: 1050,
      width: props.wrapLines ? '100%' : 'max-content',
      minWidth: '100%'
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: 0
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1050
    }),
    input: (provided: any) => ({
      ...provided,
      color: colors.text,
      fontWeight: '400',
      fontSize: '1rem',
      position: containerNoWrap ? 'absolute' : provided.position,
      opacity: containerNoWrap ? 0 : provided.opacity,
      width: containerNoWrap ? '100%' : provided.width,
      height: containerNoWrap ? '100%' : provided.height
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      transform: state.selectProps.menuIsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
      color: colors.text + '!important'
    }),
    option: (provided: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...provided,
      cursor: 'pointer',
      fontWeight: '400',
      fontSize: '1rem',
      backgroundColor:
        !useBadge && data.color && isFocused
          ? LightenDarkenColor(data.color, 90)
          : provided.backgroundColor
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: useBadge ? Colors.WHITE : provided.color
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      paddingRight: limitHeight ? '0px' : '',
      flexWrap: containerNoWrap ? 'nowrap' : 'inherit',
      paddingLeft: leftAlign ? 'inherit' : '',
      textWrap: containerNoWrap ? 'nowrap' : 'inherit',
      flexDirection: containerNoWrap ? 'row' : provided.flexDirection,
      alignItems: containerNoWrap ? 'center' : provided.alignItems,
      minHeight: containerNoWrap ? 'auto' : provided.minHeight
    })
  };

  const Control = ({ children, ...rest }: any) => (
    <components.Control {...rest}>
      {icon && <Image className="ms-3 me-2" src={icon} />} {children}
    </components.Control>
  );

  const GroupHeading = ({ children, ...rest }: any) => (
    <components.GroupHeading className="menu-group-heading" {...rest}>
      {children}
    </components.GroupHeading>
  );

  const Option = ({ children, ...rest }: any) => (
    <div
      className={`menu-option ${rest.isDisabled ? 'option-disabled' : ''} `}
      data-test={dataTestId && `${dataTestId}-option-${rest.data?.value?.replace(/\\|\//g, '')}`}
    >
      <components.Option {...rest}>{children}</components.Option>
    </div>
  );

  const getDataTestIndex = () => {
    let options = props.options;
    if (groupedOptions) {
      // this is not validated and might require changes during E2E tests
      options = (props.options as ISelectGroupedOptions[]).reduce(
        (flatOptions: any, optionGroup) => {
          return [...flatOptions, optionGroup.options];
        },
        []
      );
    }
    return options.findIndex(
      (option: any) => option.value?.replace(/\\|\//g, '') === decomposeValue()
    );
  };

  const decomposeValue = () => {
    return props.value
      ? !isArray(props.value)
        ? props.value.value?.replace(/\\|\//g, '')
        : props.value.map((item: any) => item.value?.replace(/\\|\//g, '')).join(', ')
      : null;
  };

  const MultiValueRemove = ({ children, ...rest }: any) => (
    <div className="d-contents">
      <components.MultiValueRemove {...rest}>
        <ExitPopupLightIcon
          data-test={dataTestId && `${dataTestId}-remove-${rest.data.value}`}
          className="icon font-weight-bolder"
          style={{ fontSize: '14px' }}
        />
      </components.MultiValueRemove>
    </div>
  );

  const MultiValue = ({ children, ...rest }: any) => (
    <components.MultiValue className="menu-multi-value" {...rest}>
      {children}
    </components.MultiValue>
  );

  const ValueContainer = ({ children, ...rest }: any) => (
    <components.ValueContainer
      disabled={rest.isDisabled}
      className={`menu-value-container ${rest.isDisabled ? 'disabled' : ''}`}
      {...rest}
    >
      {children}
    </components.ValueContainer>
  );

  const NoOptionsMessage = ({ children, ...rest }: any) => (
    <components.NoOptionsMessage {...rest}>
      <div data-test={dataTestId && `${dataTestId}-no-options`} className="menu-no-options">
        {children}
      </div>
    </components.NoOptionsMessage>
  );

  return (
    <div
      data-test={dataTestId}
      data-test-value={decomposeValue()}
      data-test-index={getDataTestIndex()}
    >
      {props.label && (
        <Form.Label>
          {props.label} {props.required && '*'}
        </Form.Label>
      )}
      <ReactSelect
        theme={theme}
        styles={styles}
        components={{
          Control,
          GroupHeading,
          Option,
          MultiValue,
          MultiValueRemove,
          ValueContainer,
          NoOptionsMessage
        }}
        filterOption={createFilter({
          stringify: (option) => option.label || option.value
        })}
        formatOptionLabel={(option: ISelectOption) => {
          if (useBadge) {
            return (
              <div className="btn-toolbar">
                <ClosableBadge
                  key={option.value}
                  label={option.label}
                  icon={option.icon}
                  color={option.color}
                  className="max-h-24"
                  dataTestId={`${dataTestId}-${option.value}`}
                />
              </div>
            );
          }
          if (useImage) {
            return (
              <div className="d-flex align-items-center">
                <Image
                  roundedCircle={iconRoundedCircle}
                  src={option.icon}
                  className="me-2"
                  width="20"
                  height="20"
                />
                <span data-test={`${dataTestId}-${option.value}`}>{option.label}</span>
              </div>
            );
          }
          if (useIcon) {
            return (
              <div className="d-flex align-items-center">
                <span className="me-2">{option.icon}</span>{' '}
                <span data-test={`${dataTestId}-${option.value}`}>{option.label}</span>
              </div>
            );
          }
          if (useAvatarNameColor) {
            return (
              <div className="d-flex align-items-center">
                <AvatarNameColor size={25} name={option.label} className="me-2" />
                <span data-test={`${dataTestId}-${option.value}`}>{option.label}</span>
              </div>
            );
          }
          return <span data-test={`${dataTestId}-${option.value}`}>{option.label}</span>;
        }}
        isOptionDisabled={(option) => (option.disabled ? true : false)}
        menuPosition={menuPosition}
        noOptionsMessage={() => intl.formatMessage({ id: 'noOptionsKey' })}
        menuPortalTarget={
          menuPosition === 'fixed' ? (menuPortalTarget ? menuPortalTarget : document.body) : null
        }
        {...props}
        placeholder={props.placeholder || intl.formatMessage({ id: 'selectKey' })}
      />
    </div>
  );
};

export default Dropdown;
